import cx from 'classnames'
import React, { useEffect, useRef, useState } from 'react'

import { search, times } from '../../icons'
import type { NavigationLogoViewModel } from '../../view-models/NavigationLogoViewModel'
import type { SearchBarViewModel } from '../../view-models/SearchBarViewModel'
import type { ServiceMenuItemViewModel } from '../../view-models/ServiceMenuItemViewModel'
import type { ServiceMenuViewModel } from '../../view-models/ServiceMenuViewModel'
import BaseButton from '../BaseButton/BaseButton'
import DropdownLinks from '../DropdownLinks/DropdownLinks'
import Icon from '../Icon/Icon'
import Link from '../Link/Link'
import NavigationLogo from '../NavigationLogo/NavigationLogo'
import SearchBar from '../SearchBar/SearchBar'
import styles from './serviceMenuDesktop-style.css'
import { PageLoginViewModel } from '@/modules/Navigation/view-models/PageLoginViewModel'
import PageLogin from '../PageLogin/PageLogin'
import { Button } from '@/components/Button/Button'


type Props = ServiceMenuViewModel & {
  renderLogo?: boolean
  logo: NavigationLogoViewModel
  searchBtn?: SearchBarViewModel
  onSubmit?: (...args: Array<any>) => any
  pageLogin: PageLoginViewModel
  alignLogin?: 'right' | 'left'
  onDropdownLinksUpdate?: (links: React.ReactNode, title: string) => void
  isOpened: { isOpened: boolean, lastOpened: string }
}

const ServiceMenuItem: React.FC<{
  item: ServiceMenuItemViewModel,
  onDropdownLinksUpdate: (links: React.ReactNode, title: string) => void
}> = ({ item, onDropdownLinksUpdate, isOpened }) => {
  const [dropdownVisible, setDropdownVisible] = useState(false)

  const toggleDropdown = () => {
    setDropdownVisible((prev) => !prev)

    if (onDropdownLinksUpdate) {
      const dropdownLinks = (
        <DropdownLinks items={item.links} downLink={item.downLink} cardLinks={item.cardLinks} />
      )
      onDropdownLinksUpdate(dropdownLinks, item.title)
    }
  }

  const isActive = isOpened.isOpened && isOpened.lastOpened === item.title

  if (item.links) {
    return (
      <div>
        <Button
          className={cx(styles.dropdownTrigger, isActive && styles.active)}
          onClick={toggleDropdown}
          eventMeta={dropdownVisible ? {} : { event: 'service-menu' }}
        >
          {item.title}
        </Button>
      </div>
    )
  }

  return (
    <Link key={item.title} {...item.link} className={styles.serviceMenuDesktopItem} secondary>
      {item.link ? item.link.label : item.title}
    </Link>
  )
}

const ScopeLinks: React.FC<{ links: ServiceMenuItemViewModel[] }> = ({ links }) => {
  const scopeLinks = links
    .map((item) => item.scope && { ...item.scope, label: item.scope.label, title: item.scope.title })
    .filter(Boolean)
    .reduce((acc, item) => {
      if (!acc.find((i) => i.label === item.label)) acc.push(item)
      return acc
    }, [])

  return (
    <div className={styles.scopeLinks}>
      {scopeLinks.map((scope) => (
        <Link
          key={scope.title}
          {...scope}
          className={cx(styles.serviceMenuDesktopItem, styles.scope)}
        >
          {scope.label}
        </Link>
      ))}
    </div>
  )
}

const SearchButton: React.FC<{
  searchBtn?: SearchBarViewModel
  onSearchOpen: () => void
}> = ({ searchBtn, onSearchOpen }) => {
  if (!searchBtn) return null

  return (
    <BaseButton className={styles.searchTrigger} onClick={onSearchOpen}>
      <Icon iconSrc={search} width="18px" height="18px" className={styles.icon} />
    </BaseButton>
  )
}

const PageLoginComponent = ({ pageLogin, alignLogin, onDropdownLinksUpdate }) => {
  if (!pageLogin) return null
  return (
    <PageLogin
      items={pageLogin.links}
      title={pageLogin.title}
      loggedIn={pageLogin.loggedIn}
      align={alignLogin}
      text={pageLogin.text}
      cardLinks={pageLogin.cardLinks}
      onDropdownLinksUpdate={onDropdownLinksUpdate}
    />
  )
}

const ServiceMenuDesktop: React.FC<Props & { onLinksUpdate?: (links: ServiceMenuItemViewModel[]) => void }> = ({
                                                                                                                 links,
                                                                                                                 logo,
                                                                                                                 searchBtn,
                                                                                                                 onSubmit,
                                                                                                                 pageLogin,
                                                                                                                 alignLogin,
                                                                                                                 onDropdownLinksUpdate,
                                                                                                                 isOpened,
                                                                                                               }) => {
  const [isSearchOpen, setIsSearchOpen] = useState(false)

  return (
    <div className={styles.serviceMenuDesktop}>
      <div className={styles.menuLeftContainer}>
          <NavigationLogo {...logo} className={styles.logo_small} />
        <div className={styles.menu}>
          {links.map((item) => (
            <ServiceMenuItem key={item.title} item={item} onDropdownLinksUpdate={onDropdownLinksUpdate}
                             isOpened={isOpened} />
          ))}
        </div>
      </div>
      <div className={styles.menuRightContainer}>
        <div className={styles.menuItemsRight}>
          <ScopeLinks links={links} />
          <SearchButton searchBtn={searchBtn} onSearchOpen={() => setIsSearchOpen(true)} />
        </div>
        <div className={cx(styles.searchComp, isSearchOpen ? styles.show : styles.hide)}>
          <div className={styles.searchBg}>
            <div className={styles.container}>
              <SearchBar searchBtn={searchBtn} onSubmit={onSubmit} isSearchOpen={isSearchOpen} />
              <BaseButton
                className={styles.closeSearch}
                onClick={() => setIsSearchOpen(false)}
              >
                {searchBtn && <span>{searchBtn.closeText}</span>}
                <Icon iconSrc={times} width="6px" height="24px" className={styles.closeIcon} />
              </BaseButton>
            </div>
          </div>
        </div>
          <PageLoginComponent
            pageLogin={pageLogin}
            alignLogin={alignLogin}
            onDropdownLinksUpdate={onDropdownLinksUpdate}
          />
      </div>
    </div>
  )
}

export default ServiceMenuDesktop